import { Color, SausageStyleColor } from "./enums.ts";

export const computeNewValuesOnUpdate = (
  newValue,
  index,
  values,
  lockedValues,
  minValue = 10
) => {
  if (lockedValues[index]) {
    return values;
  }

  if (values.length === 1) {
    return values;
  }

  const newValues = [...values];

  const otherIndexes = [...Array(newValues.length).keys()].filter(
    (x) => x !== index
  );
  const diff = Math.abs(newValue - values[index]);
  const increment = newValue > values[index] ? 1 : -1;
  let counter = 0;

  while (counter < diff) {
    const newIndexValue = newValues[index] + increment;
    let shouldBreak = newIndexValue < minValue;

    if (newValue === 0 || newValue === minValue) {
      shouldBreak = false;
    }

    if (shouldBreak) {
      break;
    }
    let doStop = true;

    for (let j = 0; j <= otherIndexes.length; j++) {
      const subIndex = (j + newValue + counter) % otherIndexes.length;

      const newSubIndexValue = newValues[otherIndexes[subIndex]] - increment;
      if (newSubIndexValue < minValue) {
        continue;
      }
      if (lockedValues[otherIndexes[subIndex]]) {
        continue;
      }
      doStop = false;
      newValues[otherIndexes[subIndex]] = newSubIndexValue;
      break;
    }
    if (doStop) {
      break;
    }
    newValues[index] = newIndexValue;

    counter += 1;
  }

  if (newValues.reduce((a, b) => a + b, 0) !== 100) {
    if (newValues.length === 1) {
      return newValues;
    }

    return values;
  }

  return newValues;
};

export const computeMaxValueFromValues = (values, selectedValue) => {
  let maxValue = 100;
  values.forEach((o, i) => {
    const isPendingRemoved = o.value === 0;
    if (
      selectedValue.organisation &&
      o.organisation?.id === selectedValue.organisation.id
    ) {
      maxValue -= 0;
    } else if (o.locked) {
      maxValue -= o.value;
    } else if (isPendingRemoved) {
      maxValue -= 0;
    } else {
      maxValue -= 10;
    }
  });
  return maxValue;
};

export const computeNewValuesOnAdd = (values, newValue = 10, minValue = 10) => {
  // This is for when you're adding a new organisation to the existing list.
  if (values.length === 0) {
    return [];
  }
  const newValues = [...values]; // newValues=[]
  let remaining = newValue; // remaining=10
  while (remaining > 0) {
    for (let i = 0; i < newValues.length; i++) {
      if (newValues[i] > minValue) {
        newValues[i] -= 1;
        remaining -= 1;
      }
      if (remaining === 0) {
        break;
      }
    }
  }
  return newValues;
};

export const canAddOrganisationLogic = (values, minValue = 10) => {
  let disposablePercentages = 0;
  for (let i = 0; i < values.length; i++) {
    disposablePercentages += values[i] - minValue;
  }
  return disposablePercentages >= minValue;
};

export const computePaymentPause = (currentPaymentDate, previousDate) => {
  if (previousDate === null) {
    return [false, null, null];
  }

  const diffYears =
    previousDate.getFullYear() - currentPaymentDate.getFullYear();
  const monthsDiff =
    previousDate.getMonth() + 12 * diffYears - currentPaymentDate.getMonth();

  const wasPaused = monthsDiff > 1;
  const pausedDate = new Date(currentPaymentDate);
  pausedDate.setMonth(pausedDate.getMonth() + 1);
  return [wasPaused, pausedDate, monthsDiff];
};

export const ordinal = (suffix) => {
  // This function takes a number and returns the ordinal suffix for it.
  const suffixes = ["th", "st", "nd", "rd"];
  const v = suffix % 100;
  return suffix + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

export const generateFakeFirstName = () => {
  const possibleFirstNames = [
    "Bilbo",
    "Albus",
    "Iroh",
    "Hayley",
    "Gandalf",
    "Merlin",
    "Chris",
    "Jessica",
    "Daniel",
    "Laura",
  ];

  const randomIndex = Math.floor(Math.random() * possibleFirstNames.length);
  return possibleFirstNames[randomIndex];
};

export const generateFakeLastName = () => {
  const possibleLastNames = [
    "Baggins",
    "Williams",
    "Jones",
    "Brown",
    "Davis",
    "Miller",
    "Wilson",
    "Moore",
    "Dumbledore",
    "Taylor",
  ];

  const randomIndex = Math.floor(Math.random() * possibleLastNames.length);
  return possibleLastNames[randomIndex];
};

export const generateFakeNumber = () => {
  return Math.floor(Math.random() * 999);
};

export function stringifyWithoutQuotes(obj) {
  if (Array.isArray(obj)) {
    return `[${obj.map(stringifyWithoutQuotes).join(", ")}]`;
  } else if (typeof obj === "object" && obj !== null) {
    return `{${Object.entries(obj)
      .map(([key, value]) => `${key}: ${stringifyWithoutQuotes(value)}`)
      .join(", ")}}`;
  } else if (typeof obj === "string") {
    return `"${obj}"`; // still quote string values
  } else {
    return String(obj); // for numbers, booleans, null, etc.
  }
}

export function getTailwindBackgroundColor(color) {
  if (!color) {
    return "bg-color-raindrop-primary";
  }

  // If the color is just general
  switch (color) {
    case Color.Raindrop:
      return "bg-color-raindrop-primary";
    case Color.Blue:
      return "bg-blue-600";
    case Color.Green:
      return "bg-green-600";
    case Color.Yellow:
      return "bg-yellow-500";
    case Color.Red:
      return "bg-color-red-primary";
    case Color.Bronze:
      return "bg-color-bronze-primary";
    case Color.Silver:
      return "bg-color-silver-primary";
    case Color.Gold:
      return "bg-color-gold-primary";
  }

  // If the color is a specific sausage color
  switch (color) {
    case SausageStyleColor.Blue:
      return "bg-blue-600";
    case SausageStyleColor.Green:
      return "bg-green-600";
    case SausageStyleColor.Pink:
      return "bg-color-raindrop-primary";
    case SausageStyleColor.Purple:
      return "bg-purple-600";
    case SausageStyleColor.Yellow:
      return "bg-yellow-600";
  }
}

export function getColorHex(color) {
  if (!color) {
    return "#EF5FA8";
  }
  return {
    [Color.Raindrop]: "#EF5FA8",
    [Color.Blue]: "#00A3E0",
    [Color.DarkBlue]: "#1E3A8A",
    [Color.Green]: "#49DE80",
    [Color.Yellow]: "#FACC14",
    [Color.Purple]: "#A855F7",
    [SausageStyleColor.Blue]: "#00A3E0",
    [SausageStyleColor.Green]: "#49DE80",
    [SausageStyleColor.Pink]: "#EF5FA8",
    [SausageStyleColor.Purple]: "#A855F7",
    [SausageStyleColor.Yellow]: "#CA8A03",
  }[color];
}
